import type { ReactNode } from "react";
import { BackButton } from "@/components/ui/back-button";
import { cn } from "@/lib/utils";

type ReviewPageShellProps = {
  title: string;
  headerTitle?: string;
  backFallbackHref: string;
  backLabel?: string;
  /** Rendered on the right side of the fixed header (e.g. a write-review button). */
  headerAction?: ReactNode;
  /** Extra classes for the centered content column. */
  contentClassName?: string;
  children: ReactNode;
};

/**
 * Shared glass shell for the public review surfaces. Mirrors the fixed
 * backdrop-blur header + safe-area handling used by the profile and venue
 * pages so reviews feel native to the rest of the app on mobile.
 */
export function ReviewPageShell({
  title,
  headerTitle,
  backFallbackHref,
  backLabel = "Back",
  headerAction,
  contentClassName,
  children,
}: ReviewPageShellProps) {
  return (
    <main className="min-h-dvh bg-background pb-[calc(env(safe-area-inset-bottom)+2rem)] text-text-primary dark:bg-[#0b0b0f] dark:text-white">
      <header className="fixed top-0 right-0 left-0 z-50 border-b border-border/50 bg-background/80 pt-[env(safe-area-inset-top)] backdrop-blur-md supports-backdrop-filter:bg-background/60 dark:border-glass-border-10 dark:bg-[#0b0b0f]/80 dark:supports-backdrop-filter:bg-[#0b0b0f]/60">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between gap-3 px-4">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <BackButton fallbackHref={backFallbackHref} label={backLabel} />
            <span className="truncate text-base font-semibold text-text-primary dark:text-white">
              {headerTitle ?? title}
            </span>
          </div>
          {headerAction ? (
            <div className="flex shrink-0 items-center gap-2">
              {headerAction}
            </div>
          ) : null}
        </div>
      </header>

      <div className="h-[calc(3.5rem+env(safe-area-inset-top))]" />

      <div className={cn("mx-auto w-full max-w-3xl", contentClassName)}>
        {children}
      </div>
    </main>
  );
}
